import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { useClient } from '../../api';
import type { AccountType, ClientBalanceCategoryDto } from '../../api/generated/client';
import { NavigationTabTotalWealth } from '../../enums';
import { useWealthChartData } from '../../hooks/chart/useWealthChartData';
import { wealthContext } from './wealth-context';

/**
 * Provider that delivers all necessary data for the wealth overview and balance-sheet.
 */
export const WealthProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const { data: client } = useClient();
    const wealthChartDataMap = useWealthChartData();

    const [activeTab, setActiveTab] = useState<NavigationTabTotalWealth>(NavigationTabTotalWealth.TOTAL_WEALTH);
    const [activeCategoryId, setActiveCategoryId] = useState(0);
    const [activeAccountType, setActiveAccountType] = useState<AccountType>('ASSET');
    const [activeBalanceCategory, setActiveBalanceCategory] = useState<ClientBalanceCategoryDto | undefined>(undefined);

    const onTabClicked = (tab: NavigationTabTotalWealth) => setActiveTab(tab);

    useEffect(() => {
        const getActiveBalanceCategory = () => {
            if (activeTab === NavigationTabTotalWealth.TOTAL_WEALTH) return undefined;

            return wealthChartDataMap[activeAccountType]?.visibleCategories.find((category) => category.id === activeCategoryId);
        };
        setActiveBalanceCategory(getActiveBalanceCategory());
    }, [activeCategoryId, activeTab, activeAccountType, wealthChartDataMap]);

    return (
        <wealthContext.Provider
            value={{
                activeAccountType,
                setActiveAccountType,
                activeBalanceCategory,
                setActiveCategoryId,
                activeCategoryId,
                activeTab,
                partnerUid: client?.partnerUuid,
                clientCurrency: client?.currency,
                onTabClicked,
                wealthChartDataMap,
            }}
        >
            {children}
        </wealthContext.Provider>
    );
};
